import { useEffect, useState } from 'react';
import { useTheme } from '@material-ui/core';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useQuery, gql } from '@apollo/client';
import { AlertTitle } from '@material-ui/lab';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import AutoFixHighIcon from '@material-ui/icons/Autorenew';
import RefreshIcon from '@material-ui/icons/Refresh';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import ClearIcon from '@material-ui/icons/Clear';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import LinkIcon from '@material-ui/icons/Link';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import GitHubIcon from '@material-ui/icons/GitHub';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import StorageIcon from '@material-ui/icons/Storage';
import SmartToyIcon from '@material-ui/icons/Memory';
import DnsIcon from '@material-ui/icons/Dns';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubCard,
  DevHubChip,
  DevHubCircularProgress,
  DevHubDialog,
  DevHubDialogActions,
  DevHubDialogContent,
  DevHubDialogTitle,
  DevHubDivider,
  DevHubGrid,
  DevHubIconButton,
  DevHubMenu,
  DevHubMenuItem,
  DevHubSnackbar,
  DevHubSwitch,
  DevHubTextField,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';

import {
  type ChannelExecutionMode,
  type ContextEntry,
  type ContextType,
  CONTEXT_TYPE_LABELS,
  entryLabel,
  entryChipColor,
  useInstructions,
  useChannelEntries,
  useCredentials,
  useEntryCredentials,
  useFlowPools,
  useMcpServer,
  useContextAdmins,
  useChannelPolicy,
  AddContextDialog,
  FlowPoolPickerDialog,
  McpServerPickerDialog,
} from './nova-slack';
import { EntryCredentialsFields } from './nova-slack/EntryCredentialsFields';
import { PartySearch } from '../common/PartySearch';
import { ResponsibleParty } from '../insights/types/RoleTypes';

/** GraphQL query to check UpdateApplicationPolicy permission */
const GET_APPLICATION_POLICY = gql`
  query GetApplicationPolicy(
    $applicationId: Int!
    $filter: AuthorizationPolicyFilter!
  ) {
    resources {
      application(applicationId: $applicationId) {
        authorizationPolicy(filter: $filter) {
          policy
          hasAccess
        }
      }
    }
  }
`;

/** Nova settings panel for an app entity — manages per-channel context and Slack instructions. */
export const AppNovaSettings = () => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const { entity } = useEntity();
  const theme = useTheme() as any;

  const appId = entity?.metadata?.annotations?.['comcast.net/itrc-id'];
  const backendUrl = config.getString('backend.baseUrl');

  // Permission check
  const {
    data: policyData,
    loading: checkingPolicy,
    error: policyError,
  } = useQuery(GET_APPLICATION_POLICY, {
    skip: !appId,
    variables: {
      applicationId: appId ? parseInt(appId as string, 10) : 0,
      filter: {
        policies: ['UpdateApplicationPolicy'],
      },
    },
  });

  // Hooks
  const instrHook = useInstructions(backendUrl, appId);
  const entriesHook = useChannelEntries(backendUrl);
  const credHook = useCredentials(backendUrl);
  const entryCredHook = useEntryCredentials(backendUrl);
  const flowHook = useFlowPools(backendUrl);
  const mcpHook = useMcpServer(backendUrl);
  const adminsHook = useContextAdmins(backendUrl);
  const policyHook = useChannelPolicy(backendUrl);

  // Entry credentials dialog state
  const [entryCredDialogEntry, setEntryCredDialogEntry] = useState<{
    entryKey: string;
    label: string;
  } | null>(null);
  const [editingEntry, setEditingEntry] = useState<{
    index: number;
    entry: ContextEntry;
  } | null>(null);
  const [editingSaving, setEditingSaving] = useState(false);
  const [editingFileName, setEditingFileName] = useState<string | null>(null);
  const [editingFileObj, setEditingFileObj] = useState<File | null>(null);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [selectedAdminParty, setSelectedAdminParty] =
    useState<ResponsibleParty>({} as ResponsibleParty);
  const [adminSearchKey, setAdminSearchKey] = useState(0);
  const [currentUserEmail, setCurrentUserEmail] = useState('');

  // Permission state
  const [canManageAdmins, setCanManageAdmins] = useState<boolean>(false);

  // Channel state
  const [linkedChannels, setLinkedChannels] = useState<string[]>([]);
  const [channelNames, setChannelNames] = useState<Record<string, string>>({});
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Add context dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState<ContextType>('url');
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);

  // Combine errors from hooks
  const combinedError =
    fetchError || instrHook.error || entriesHook.error || policyHook.error;
  const clearError = () => {
    setFetchError(null);
    instrHook.setError(null);
    entriesHook.setError(null);
    policyHook.setError(null);
  };

  const fetchSettings = async () => {
    if (!appId) return;
    try {
      const { token } = await identityApi.getCredentials();
      const headers: Record<string, string> = token
        ? { Authorization: `Bearer ${token}` }
        : {};
      const res = await fetch(
        `${backendUrl}/api/agent/app/${appId}/slack-settings`,
        { headers },
      );
      if (!res.ok) {
        setFetchError('Failed to load Nova settings.');
        return;
      }
      const data = await res.json();
      instrHook.setInstructions(
        (data.instructions ?? []).map((i: any) =>
          typeof i === 'string' ? { text: i, active: true } : i,
        ),
      );
      const channels: string[] = data.channels ?? [];
      setLinkedChannels(channels);
      setChannelNames(data.channelNames ?? {});
      if (channels.length > 0) {
        setSelectedChannel('__all__');
      }
    } catch {
      setFetchError('Failed to load Nova settings.');
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  useEffect(() => {
    const loadCurrentUserEmail = async () => {
      try {
        const profile = await identityApi.getProfileInfo();
        setCurrentUserEmail((profile?.email ?? '').toLowerCase());
      } catch {
        setCurrentUserEmail('');
      }
    };

    loadCurrentUserEmail();
  }, [identityApi]);

  useEffect(() => {
    if (!appId) {
      setCanManageAdmins(true);
      return;
    }

    if (checkingPolicy) {
      return;
    }

    if (policyError) {
      setCanManageAdmins(false);
      return;
    }

    if (policyData?.resources?.application?.authorizationPolicy) {
      const updatePolicy =
        policyData.resources.application.authorizationPolicy.find(
          (p: any) => p.policy === 'UpdateApplicationPolicy',
        );
      setCanManageAdmins(updatePolicy?.hasAccess ?? false);
    }
  }, [policyData, checkingPolicy, policyError, appId]);

  const handleSelectChannel = (channelId: string) => {
    setSelectedChannel(channelId);
    entriesHook.load(channelId);
    credHook.loadStatus(channelId);
    adminsHook.load(channelId);
    policyHook.load(channelId);
  };

  const numericAppId = appId ? parseInt(appId as string, 10) : undefined;

  const saveExecutionMode = async (nextMode: ChannelExecutionMode) => {
    if (!selectedChannel || selectedChannel === '__all__') return;
    await policyHook.save(selectedChannel, nextMode, numericAppId);
  };

  const saveChannelEntries = async (next: ContextEntry[]) => {
    if (!selectedChannel || selectedChannel === '__all__') return;
    await entriesHook.save(selectedChannel, next, undefined, numericAppId);
  };

  const openAddDialog = (type: ContextType) => {
    setAddType(type);
    setAddOpen(true);
  };

  const closeEditDialog = () => {
    setEditingEntry(null);
    setEditingFileName(null);
    setEditingFileObj(null);
  };

  const handleEditFileChange = (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditingFileObj(file);
    setEditingFileName(file.name);
  };

  const selectedAdminEmail = (selectedAdminParty?.email ?? '')
    .trim()
    .toLowerCase();
  const canEditSelectedChannelContext =
    selectedChannel !== '__all__' &&
    !!selectedChannel &&
    (canManageAdmins ||
      (!!currentUserEmail && adminsHook.admins.includes(currentUserEmail)));

  // Helper to resolve row background based on active state and theme mode
  function activeRowBg(active: boolean, isDark: boolean, activeColor: string) {
    if (active)
      return isDark ? `rgba(${activeColor},0.06)` : `rgba(${activeColor},0.04)`;
    return isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)';
  }

  if (!appId) {
    return (
      <DevHubBox p={3}>
        <DevHubAlert severity="warning">
          This entity has no <code>comcast.net/itrc-id</code> annotation — Nova
          settings are unavailable.
        </DevHubAlert>
      </DevHubBox>
    );
  }

  return (
    <DevHubGrid container spacing={3}>
      {combinedError && (
        <DevHubGrid item xs={12}>
          <DevHubAlert severity="error" onClose={clearError}>
            {combinedError}
          </DevHubAlert>
        </DevHubGrid>
      )}

      {/* ── Channel Context ── */}
      <DevHubGrid item xs={12}>
        <DevHubCard
          density="roomy"
          title="Slack Channel Configuration"
          subTitle="Manage context sources, instructions, and credentials per linked Slack channel. App owners can provide SAT credentials for external integrations. Use 'ALL Channels' to set instructions that apply everywhere."
          body={
            linkedChannels.length === 0 ? (
              <DevHubTypography variant="body2" style={{ opacity: 0.5 }}>
                No channels linked yet. Use{' '}
                <code>@nova-bot link-app {appId}</code> in Slack.
              </DevHubTypography>
            ) : (
              <DevHubBox
                display="flex"
                flexDirection="column"
                style={{ gap: 16 }}
              >
                {/* Channel selector */}
                <DevHubBox display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                  <DevHubBox
                    style={{
                      opacity: selectedChannel === '__all__' ? 1 : 0.45,
                    }}
                  >
                    <DevHubButton
                      size="small"
                      variant={
                        selectedChannel === '__all__' ? 'contained' : 'outlined'
                      }
                      onClick={() => {
                        setSelectedChannel('__all__');
                        entriesHook.setChannelEntries([]);
                        policyHook.setMode('default');
                      }}
                      buttonColor={
                        selectedChannel === '__all__' ? 'blueGreen' : undefined
                      }
                      textColor={
                        selectedChannel === '__all__' ? 'white' : undefined
                      }
                    >
                      ALL Channels
                    </DevHubButton>
                  </DevHubBox>
                  {linkedChannels.map(ch => (
                    <DevHubBox
                      key={ch}
                      style={{
                        opacity: selectedChannel === ch ? 1 : 0.45,
                      }}
                    >
                      <DevHubButton
                        size="small"
                        variant={
                          selectedChannel === ch ? 'contained' : 'outlined'
                        }
                        onClick={() => handleSelectChannel(ch)}
                        buttonColor={
                          selectedChannel === ch ? 'purple' : undefined
                        }
                        textColor={selectedChannel === ch ? 'white' : undefined}
                      >
                        {channelNames[ch] ? `#${channelNames[ch]}` : `#${ch}`}
                      </DevHubButton>
                    </DevHubBox>
                  ))}
                </DevHubBox>

                {/* ── ALL Channels: App-level instructions ── */}
                {selectedChannel === '__all__' && (
                  <>
                    <DevHubDivider />
                    <DevHubTypography variant="subtitle2">
                      App Instructions — applied to every linked channel
                    </DevHubTypography>

                    <DevHubTextField
                      multiline
                      minRows={1}
                      hiddenLabel
                      fullWidth
                      value={instrHook.newInstruction}
                      onChange={(e: any) => {
                        instrHook.setNewInstruction(e.target.value);
                        instrHook.setRewrittenDraft(null);
                      }}
                      placeholder="Add a new instruction…"
                      style={{ paddingRight: '2rem' }}
                      InputProps={{
                        endAdornment: instrHook.newInstruction ? (
                          <DevHubIconButton
                            size="small"
                            onClick={() => {
                              instrHook.setNewInstruction('');
                              instrHook.setRewrittenDraft(null);
                            }}
                          >
                            <ClearIcon fontSize="small" />
                          </DevHubIconButton>
                        ) : undefined,
                      }}
                    />
                    {instrHook.rewrittenDraft && (
                      <DevHubAlert severity="info">
                        <AlertTitle>Suggested rewrite</AlertTitle>
                        <DevHubBox
                          style={{
                            fontFamily: 'monospace',
                            fontSize: 13,
                            margin: '8px 0',
                          }}
                        >
                          {instrHook.rewrittenDraft}
                        </DevHubBox>
                        <DevHubButton
                          variant="outlined"
                          color="primary"
                          height="small"
                          onClick={() => {
                            instrHook.setNewInstruction(
                              instrHook.rewrittenDraft!,
                            );
                            instrHook.setRewrittenDraft(null);
                          }}
                        >
                          Use this
                        </DevHubButton>
                      </DevHubAlert>
                    )}
                    <DevHubBox
                      display="flex"
                      justifyContent="flex-end"
                      style={{ gap: 8 }}
                    >
                      <DevHubButton
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={instrHook.rewrite}
                        disabled={
                          instrHook.rewriting ||
                          !instrHook.newInstruction.trim()
                        }
                        startIcon={
                          instrHook.rewriting ? (
                            <DevHubCircularProgress size={14} />
                          ) : (
                            <AutoFixHighIcon fontSize="small" />
                          )
                        }
                      >
                        {instrHook.rewriting ? 'Rewriting…' : 'Improve with AI'}
                      </DevHubButton>
                      <DevHubButton
                        variant="contained"
                        buttonColor="blueGreen"
                        textColor="white"
                        size="small"
                        onClick={instrHook.add}
                        disabled={
                          instrHook.saving || !instrHook.newInstruction.trim()
                        }
                        startIcon={
                          instrHook.saving ? (
                            <DevHubCircularProgress size={14} />
                          ) : (
                            <AddIcon fontSize="small" />
                          )
                        }
                      >
                        {instrHook.saving ? 'Saving…' : 'Add'}
                      </DevHubButton>
                    </DevHubBox>

                    {/* List of saved instructions */}
                    {instrHook.instructions.length > 0 ? (
                      <DevHubBox
                        display="flex"
                        flexDirection="column"
                        style={{ gap: 8 }}
                      >
                        {instrHook.instructions.map((inst, i) => (
                          <DevHubBox
                            key={i}
                            display="flex"
                            alignItems="center"
                            style={{
                              gap: 12,
                              padding: '10px 14px',
                              borderRadius: 8,
                              border: `1px solid ${inst.active ? (theme.palette.devhub?.blueGreen ?? '#00bfa5') : '#e0e0e0'}`,
                              background: activeRowBg(
                                inst.active,
                                theme.palette.type === 'dark',
                                '0,191,165',
                              ),
                              opacity: inst.active ? 1 : 0.6,
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <DevHubSwitch
                              checked={inst.active}
                              size="small"
                              onChange={() => instrHook.toggle(i)}
                            />
                            <DevHubChip
                              label={inst.active ? 'Active' : 'Inactive'}
                              chipColor={inst.active ? 'success' : 'medGray'}
                              chipSize="small"
                              noMargins
                            />
                            <DevHubTypography
                              variant="body2"
                              style={{
                                flex: 1,
                                whiteSpace: 'pre-wrap',
                                textDecoration: inst.active
                                  ? 'none'
                                  : 'line-through',
                                maxHeight: 60,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {inst.text}
                            </DevHubTypography>
                            <DevHubTooltip title="Remove" placement="top">
                              <DevHubIconButton
                                size="small"
                                onClick={() => instrHook.remove(i)}
                              >
                                <DeleteIcon fontSize="small" />
                              </DevHubIconButton>
                            </DevHubTooltip>
                          </DevHubBox>
                        ))}
                      </DevHubBox>
                    ) : (
                      <DevHubTypography
                        variant="body2"
                        style={{ opacity: 0.5 }}
                      >
                        No instructions yet. Add one above to customize Nova's
                        behavior across all channels.
                      </DevHubTypography>
                    )}
                  </>
                )}

                {/* ── Channel Credentials ── */}
                {selectedChannel &&
                  selectedChannel !== '__all__' &&
                  ConfigHelper.instance.isFeatureEnabled(
                    Feature.CHANNEL_CREDENTIALS,
                    false,
                  ) && (
                    <DevHubBox
                      display="flex"
                      flexDirection="column"
                      style={{
                        borderLeft: `3px solid ${theme.palette.devhub.purple}`,
                        borderRadius: '0 8px 8px 0',
                        background:
                          theme.palette.type === 'dark'
                            ? 'rgba(255,255,255,0.03)'
                            : 'rgba(0,0,0,0.02)',
                        padding: '10px 16px',
                        gap: 0,
                      }}
                    >
                      <DevHubBox
                        display="flex"
                        alignItems="center"
                        style={{ gap: 8, cursor: 'pointer' }}
                        onClick={() =>
                          credHook.setCredExpanded(!credHook.credExpanded)
                        }
                      >
                        <LockOutlinedIcon
                          fontSize="small"
                          style={{ opacity: 0.6 }}
                        />
                        <DevHubTypography
                          variant="subtitle2"
                          style={{ flex: 1 }}
                        >
                          Credentials
                        </DevHubTypography>
                        {credHook.hasCredentials && !credHook.credExpanded && (
                          <DevHubChip
                            label="Configured"
                            chipColor="success"
                            chipSize="small"
                            noMargins
                          />
                        )}
                        {credHook.credExpanded ? (
                          <ExpandLessIcon
                            fontSize="small"
                            style={{ opacity: 0.5 }}
                          />
                        ) : (
                          <ExpandMoreIcon
                            fontSize="small"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </DevHubBox>

                      {credHook.credExpanded && (
                        <DevHubBox
                          display="flex"
                          flexDirection="column"
                          style={{ gap: 12, marginTop: 12 }}
                        >
                          <DevHubTypography
                            variant="caption"
                            style={{ opacity: 0.6 }}
                          >
                            SAT token used for FLOW:Context and external agent
                            calls in{' '}
                            <strong>
                              {channelNames[selectedChannel]
                                ? `#${channelNames[selectedChannel]}`
                                : `#${selectedChannel}`}
                            </strong>
                            . Only app owners can configure this.
                          </DevHubTypography>
                          {credHook.credError && (
                            <DevHubAlert
                              severity="error"
                              onClose={() => credHook.setCredError(null)}
                            >
                              {credHook.credError}
                            </DevHubAlert>
                          )}
                          {credHook.hasCredentials === null && (
                            <DevHubCircularProgress size={20} />
                          )}
                          {credHook.hasCredentials !== null &&
                            (credHook.hasCredentials ? (
                              <DevHubBox
                                display="flex"
                                alignItems="center"
                                style={{ gap: 12 }}
                              >
                                <DevHubChip
                                  label="SAT token configured"
                                  chipColor="success"
                                  chipSize="small"
                                  noMargins
                                />
                                <DevHubButton
                                  variant="outlined"
                                  color="secondary"
                                  size="small"
                                  onClick={() =>
                                    credHook.setConfirmRemoveOpen(true)
                                  }
                                  disabled={credHook.credRemoving}
                                  startIcon={
                                    credHook.credRemoving ? (
                                      <DevHubCircularProgress size={14} />
                                    ) : (
                                      <DeleteIcon fontSize="small" />
                                    )
                                  }
                                >
                                  {credHook.credRemoving
                                    ? 'Removing…'
                                    : 'Remove'}
                                </DevHubButton>
                              </DevHubBox>
                            ) : (
                              <DevHubBox
                                display="flex"
                                flexDirection="column"
                                style={{ gap: 12 }}
                              >
                                <DevHubTextField
                                  label="Token URL"
                                  fullWidth
                                  value={credHook.credTokenUrl}
                                  onChange={(e: any) => {
                                    credHook.setCredTokenUrl(e.target.value);
                                    credHook.setCredTokenUrlError(null);
                                  }}
                                  placeholder="https://sat.example.com/oauth/token"
                                  size="small"
                                  error={!!credHook.credTokenUrlError}
                                  helperText={credHook.credTokenUrlError}
                                />
                                <DevHubTextField
                                  label="Client ID"
                                  fullWidth
                                  value={credHook.credClientId}
                                  onChange={(e: any) =>
                                    credHook.setCredClientId(e.target.value)
                                  }
                                  size="small"
                                />
                                <DevHubTextField
                                  label="Client Secret"
                                  type="password"
                                  fullWidth
                                  value={credHook.credClientSecret}
                                  onChange={(e: any) =>
                                    credHook.setCredClientSecret(e.target.value)
                                  }
                                  size="small"
                                />
                                <DevHubTextField
                                  label="Scopes"
                                  fullWidth
                                  value={credHook.credScopes}
                                  onChange={(e: any) =>
                                    credHook.setCredScopes(e.target.value)
                                  }
                                  placeholder="scope1,scope2"
                                  size="small"
                                  helperText="Comma-separated list of OAuth scopes"
                                />
                                <DevHubBox
                                  display="flex"
                                  justifyContent="flex-end"
                                >
                                  <DevHubButton
                                    variant="contained"
                                    buttonColor="blueGreen"
                                    textColor="white"
                                    size="small"
                                    onClick={() =>
                                      credHook.saveCredential(selectedChannel)
                                    }
                                    disabled={
                                      credHook.credSaving ||
                                      !credHook.credTokenUrl.trim() ||
                                      !credHook.credClientId.trim() ||
                                      !credHook.credClientSecret.trim()
                                    }
                                    startIcon={
                                      credHook.credSaving ? (
                                        <DevHubCircularProgress size={14} />
                                      ) : undefined
                                    }
                                  >
                                    {credHook.credSaving ? 'Saving…' : 'Save'}
                                  </DevHubButton>
                                </DevHubBox>
                              </DevHubBox>
                            ))}
                        </DevHubBox>
                      )}
                    </DevHubBox>
                  )}

                {/* Context Admin Management */}
                {selectedChannel &&
                  selectedChannel !== '__all__' &&
                  canManageAdmins && (
                    <DevHubBox
                      display="flex"
                      flexDirection="column"
                      style={{
                        borderLeft: `3px solid ${theme.palette.devhub?.purple ?? '#7c4dff'}`,
                        borderRadius: '0 8px 8px 0',
                        background:
                          theme.palette.type === 'dark'
                            ? 'rgba(255,255,255,0.03)'
                            : 'rgba(0,0,0,0.02)',
                        padding: '10px 16px',
                        gap: 12,
                      }}
                    >
                      <DevHubBox
                        display="flex"
                        alignItems="center"
                        style={{ gap: 8, cursor: 'pointer' }}
                        onClick={() => setAdminExpanded(!adminExpanded)}
                      >
                        <LockOutlinedIcon
                          fontSize="small"
                          style={{ opacity: 0.6 }}
                        />
                        <DevHubTypography
                          variant="subtitle2"
                          style={{ flex: 1 }}
                        >
                          Context Admins
                        </DevHubTypography>
                        {adminsHook.admins.length > 0 &&
                          !adminsHook.loadingAdmins &&
                          !adminExpanded && (
                            <DevHubChip
                              label={`${adminsHook.admins.length} admin${adminsHook.admins.length !== 1 ? 's' : ''}`}
                              chipColor="info"
                              chipSize="small"
                              noMargins
                            />
                          )}
                        {adminExpanded ? (
                          <ExpandLessIcon
                            fontSize="small"
                            style={{ opacity: 0.5 }}
                          />
                        ) : (
                          <ExpandMoreIcon
                            fontSize="small"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </DevHubBox>

                      {adminExpanded && (
                        <DevHubBox
                          display="flex"
                          flexDirection="column"
                          style={{ gap: 12 }}
                        >
                          <DevHubTypography
                            variant="caption"
                            style={{ opacity: 0.6 }}
                          >
                            Control who can add, modify, or remove channel
                            context and linked apps in{' '}
                            <strong>
                              {channelNames[selectedChannel]
                                ? `#${channelNames[selectedChannel]}`
                                : `#${selectedChannel}`}
                            </strong>
                            .
                          </DevHubTypography>

                          {adminsHook.adminError && (
                            <DevHubAlert
                              severity="error"
                              onClose={() => adminsHook.setAdminError(null)}
                            >
                              {adminsHook.adminError}
                            </DevHubAlert>
                          )}

                          {adminsHook.loadingAdmins ? (
                            <DevHubCircularProgress size={20} />
                          ) : (
                            <DevHubBox
                              display="flex"
                              flexDirection="column"
                              style={{ gap: 12 }}
                            >
                              {/* Add admin section */}
                              <DevHubGrid
                                container
                                spacing={1}
                                alignItems="center"
                              >
                                <DevHubGrid item xs={12} sm={8} md={7} lg={6}>
                                  <DevHubBox style={{ maxWidth: 560 }}>
                                    <PartySearch
                                      key={adminSearchKey}
                                      label="Search for a user"
                                      helperText="Select a user to grant context admin permissions"
                                      setSelectedParty={
                                        setSelectedAdminParty as any
                                      }
                                      selectedParty={selectedAdminParty}
                                      required={false}
                                    />
                                  </DevHubBox>
                                </DevHubGrid>
                                <DevHubGrid item xs="auto">
                                  <DevHubBox
                                    style={{ minWidth: 84, marginBottom: 22 }}
                                  >
                                    <DevHubButton
                                      variant="contained"
                                      buttonColor={
                                        adminsHook.adminSaving ||
                                        !selectedAdminEmail
                                          ? undefined
                                          : 'blueGreen'
                                      }
                                      textColor={
                                        adminsHook.adminSaving ||
                                        !selectedAdminEmail
                                          ? undefined
                                          : 'white'
                                      }
                                      size="large"
                                      onClick={async () => {
                                        const added = await adminsHook.addAdmin(
                                          selectedChannel,
                                          selectedAdminEmail,
                                          numericAppId,
                                        );
                                        if (added) {
                                          setSelectedAdminParty(
                                            {} as ResponsibleParty,
                                          );
                                          setAdminSearchKey(prev => prev + 1);
                                        }
                                      }}
                                      disabled={
                                        adminsHook.adminSaving ||
                                        !selectedAdminEmail
                                      }
                                      startIcon={
                                        adminsHook.adminSaving ? (
                                          <DevHubCircularProgress size={14} />
                                        ) : (
                                          <AddIcon fontSize="small" />
                                        )
                                      }
                                    >
                                      Add
                                    </DevHubButton>
                                  </DevHubBox>
                                </DevHubGrid>
                              </DevHubGrid>
                              {!selectedAdminEmail && (
                                <DevHubTypography
                                  variant="caption"
                                  style={{ opacity: 0.6 }}
                                >
                                  Select a user with a valid email to add as
                                  admin.
                                </DevHubTypography>
                              )}

                              {/* List of admins */}
                              {adminsHook.admins.length > 0 ? (
                                <DevHubBox
                                  display="flex"
                                  flexDirection="column"
                                  style={{ gap: 6 }}
                                >
                                  {adminsHook.admins.map((admin, i) => (
                                    <DevHubBox
                                      key={i}
                                      display="flex"
                                      alignItems="center"
                                      style={{
                                        gap: 12,
                                        padding: '8px 12px',
                                        borderRadius: 6,
                                        border: `1px solid ${theme.palette.devhub?.blueGreen ?? '#00bfa5'}`,
                                        background:
                                          theme.palette.type === 'dark'
                                            ? 'rgba(0,191,165,0.06)'
                                            : 'rgba(0,191,165,0.04)',
                                      }}
                                    >
                                      <DevHubTypography
                                        variant="body2"
                                        style={{ flex: 1 }}
                                      >
                                        {admin}
                                      </DevHubTypography>
                                      <DevHubTooltip
                                        title="Remove admin"
                                        placement="top"
                                      >
                                        <DevHubIconButton
                                          size="small"
                                          onClick={() =>
                                            adminsHook.removeAdmin(
                                              selectedChannel,
                                              admin,
                                              numericAppId,
                                            )
                                          }
                                          disabled={
                                            adminsHook.adminRemoving ||
                                            adminsHook.adminSaving
                                          }
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </DevHubIconButton>
                                      </DevHubTooltip>
                                    </DevHubBox>
                                  ))}
                                </DevHubBox>
                              ) : (
                                <DevHubTypography
                                  variant="body2"
                                  style={{ opacity: 0.5 }}
                                >
                                  No admins yet. Add one above to grant
                                  permissions for this channel.
                                </DevHubTypography>
                              )}
                            </DevHubBox>
                          )}
                        </DevHubBox>
                      )}
                    </DevHubBox>
                  )}
                {selectedChannel && selectedChannel !== '__all__' && (
                  <DevHubBox
                    display="flex"
                    flexDirection="column"
                    style={{
                      borderLeft: `3px solid ${theme.palette.devhub?.purple ?? '#7c4dff'}`,
                      borderRadius: '0 8px 8px 0',
                      background:
                        theme.palette.type === 'dark'
                          ? 'rgba(124,77,255,0.08)'
                          : 'rgba(124,77,255,0.05)',
                      padding: '10px 16px',
                      gap: 10,
                    }}
                  >
                    <DevHubBox
                      display="flex"
                      alignItems="center"
                      style={{ gap: 8 }}
                    >
                      <DevHubTypography variant="subtitle2">
                        Context Execution Mode
                      </DevHubTypography>
                      <DevHubChip
                        label={
                          policyHook.mode === 'channel_only'
                            ? 'Channel-only'
                            : 'Default'
                        }
                        chipColor={
                          policyHook.mode === 'channel_only' ? 'purple' : 'info'
                        }
                        chipSize="small"
                        noMargins
                      />
                    </DevHubBox>
                    <DevHubTypography
                      variant="caption"
                      style={{ opacity: 0.75 }}
                    >
                      Channel-only mode opts this channel out of built-in DevHub
                      context and default tooling. Nova will use only context
                      and integrations explicitly added to this channel.
                    </DevHubTypography>
                    <DevHubBox
                      display="flex"
                      alignItems="center"
                      style={{ gap: 10 }}
                    >
                      <DevHubSwitch
                        size="small"
                        checked={policyHook.mode === 'channel_only'}
                        disabled={
                          !canEditSelectedChannelContext ||
                          policyHook.loading ||
                          policyHook.saving
                        }
                        onChange={() =>
                          saveExecutionMode(
                            policyHook.mode === 'channel_only'
                              ? 'default'
                              : 'channel_only',
                          )
                        }
                      />
                      <DevHubTypography
                        variant="body2"
                        style={{ opacity: 0.8 }}
                      >
                        Use only channel-provided context and tooling
                      </DevHubTypography>
                      {policyHook.saving && (
                        <DevHubCircularProgress size={14} />
                      )}
                    </DevHubBox>
                    {!canEditSelectedChannelContext && (
                      <DevHubTypography
                        variant="caption"
                        style={{ opacity: 0.6 }}
                      >
                        Changing this mode requires UpdateApplicationPolicy or
                        channel admin access.
                      </DevHubTypography>
                    )}
                  </DevHubBox>
                )}
                {selectedChannel && selectedChannel !== '__all__' && (
                  <>
                    <DevHubDivider />
                    <DevHubBox
                      display="flex"
                      alignItems="center"
                      style={{ gap: 8 }}
                    >
                      <DevHubTypography variant="subtitle2">
                        Context for{' '}
                        {channelNames[selectedChannel]
                          ? `#${channelNames[selectedChannel]}`
                          : `#${selectedChannel}`}
                        :
                      </DevHubTypography>
                      <DevHubBox flex={1} />
                      {canEditSelectedChannelContext ? (
                        <DevHubButton
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<AddIcon fontSize="small" />}
                          onClick={e => setAddMenuAnchor(e.currentTarget)}
                        >
                          Add Context
                        </DevHubButton>
                      ) : (
                        <DevHubTypography
                          variant="caption"
                          style={{ opacity: 0.6 }}
                        >
                          Context edits require UpdateApplicationPolicy or
                          channel admin access.
                        </DevHubTypography>
                      )}
                      <DevHubMenu
                        anchorEl={addMenuAnchor}
                        open={Boolean(addMenuAnchor)}
                        onClose={() => setAddMenuAnchor(null)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <DevHubBox px={2} pt={1} pb={0.5}>
                          <DevHubTypography
                            variant="caption"
                            style={{
                              opacity: 0.5,
                              textTransform: 'uppercase',
                              letterSpacing: 1,
                            }}
                          >
                            Add context source
                          </DevHubTypography>
                        </DevHubBox>
                        <DevHubMenuItem
                          onClick={() => {
                            setAddMenuAnchor(null);
                            openAddDialog('url');
                          }}
                        >
                          <DevHubBox
                            display="flex"
                            alignItems="center"
                            style={{ gap: 12 }}
                          >
                            <LinkIcon
                              fontSize="small"
                              style={{
                                color:
                                  theme.palette.devhub?.blueGreen ?? '#00bfa5',
                              }}
                            />
                            <DevHubBox>
                              <DevHubTypography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                URL
                              </DevHubTypography>
                              <DevHubTypography
                                variant="caption"
                                style={{ opacity: 0.6 }}
                              >
                                Web page or API docs
                              </DevHubTypography>
                            </DevHubBox>
                          </DevHubBox>
                        </DevHubMenuItem>
                        <DevHubMenuItem
                          onClick={() => {
                            setAddMenuAnchor(null);
                            openAddDialog('text');
                          }}
                        >
                          <DevHubBox
                            display="flex"
                            alignItems="center"
                            style={{ gap: 12 }}
                          >
                            <TextFieldsIcon
                              fontSize="small"
                              style={{ color: '#4caf50' }}
                            />
                            <DevHubBox>
                              <DevHubTypography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                Text
                              </DevHubTypography>
                              <DevHubTypography
                                variant="caption"
                                style={{ opacity: 0.6 }}
                              >
                                Inline knowledge snippet
                              </DevHubTypography>
                            </DevHubBox>
                          </DevHubBox>
                        </DevHubMenuItem>
                        <DevHubMenuItem
                          onClick={() => {
                            setAddMenuAnchor(null);
                            openAddDialog('github');
                          }}
                        >
                          <DevHubBox
                            display="flex"
                            alignItems="center"
                            style={{ gap: 12 }}
                          >
                            <GitHubIcon
                              fontSize="small"
                              style={{
                                color:
                                  theme.palette.type === 'dark'
                                    ? '#fff'
                                    : '#24292f',
                              }}
                            />
                            <DevHubBox>
                              <DevHubTypography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                GitHub Docs
                              </DevHubTypography>
                              <DevHubTypography
                                variant="caption"
                                style={{ opacity: 0.6 }}
                              >
                                Index markdown from a repo
                              </DevHubTypography>
                            </DevHubBox>
                          </DevHubBox>
                        </DevHubMenuItem>
                        <DevHubMenuItem
                          onClick={() => {
                            setAddMenuAnchor(null);
                            openAddDialog('file');
                          }}
                        >
                          <DevHubBox
                            display="flex"
                            alignItems="center"
                            style={{ gap: 12 }}
                          >
                            <AttachFileIcon
                              fontSize="small"
                              style={{ color: '#78909c' }}
                            />
                            <DevHubBox>
                              <DevHubTypography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                File
                              </DevHubTypography>
                              <DevHubTypography
                                variant="caption"
                                style={{ opacity: 0.6 }}
                              >
                                Upload PDF, text, or YAML
                              </DevHubTypography>
                            </DevHubBox>
                          </DevHubBox>
                        </DevHubMenuItem>
                        <DevHubDivider style={{ margin: '4px 0' }} />
                        <DevHubMenuItem
                          onClick={() => {
                            setAddMenuAnchor(null);
                            flowHook.open();
                          }}
                        >
                          <DevHubBox
                            display="flex"
                            alignItems="center"
                            style={{ gap: 12 }}
                          >
                            <StorageIcon
                              fontSize="small"
                              style={{
                                color:
                                  theme.palette.devhub?.purple ?? '#7c4dff',
                              }}
                            />
                            <DevHubBox>
                              <DevHubTypography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                FLOW:Context
                              </DevHubTypography>
                              <DevHubTypography
                                variant="caption"
                                style={{ opacity: 0.6 }}
                              >
                                Link a managed context pool
                              </DevHubTypography>
                            </DevHubBox>
                          </DevHubBox>
                        </DevHubMenuItem>
                        <DevHubMenuItem
                          onClick={() => {
                            setAddMenuAnchor(null);
                            openAddDialog('agent');
                          }}
                        >
                          <DevHubBox
                            display="flex"
                            alignItems="center"
                            style={{ gap: 12 }}
                          >
                            <SmartToyIcon
                              fontSize="small"
                              style={{ color: '#e040fb' }}
                            />
                            <DevHubBox>
                              <DevHubTypography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                Agent
                              </DevHubTypography>
                              <DevHubTypography
                                variant="caption"
                                style={{ opacity: 0.6 }}
                              >
                                Connect an external A2A agent
                              </DevHubTypography>
                            </DevHubBox>
                          </DevHubBox>
                        </DevHubMenuItem>
                        <DevHubMenuItem
                          onClick={() => {
                            setAddMenuAnchor(null);
                            mcpHook.open();
                          }}
                        >
                          <DevHubBox
                            display="flex"
                            alignItems="center"
                            style={{ gap: 12 }}
                          >
                            <DnsIcon
                              fontSize="small"
                              style={{ color: '#00897b' }}
                            />
                            <DevHubBox>
                              <DevHubTypography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                MCP Server
                              </DevHubTypography>
                              <DevHubTypography
                                variant="caption"
                                style={{ opacity: 0.6 }}
                              >
                                Connect a direct MCP tool server
                              </DevHubTypography>
                            </DevHubBox>
                          </DevHubBox>
                        </DevHubMenuItem>
                      </DevHubMenu>
                    </DevHubBox>

                    {/* Context type legend */}
                    <DevHubBox
                      display="flex"
                      justifyContent="flex-end"
                      alignItems="center"
                      flexWrap="wrap"
                      style={{ gap: 6 }}
                    >
                      <DevHubTypography
                        variant="caption"
                        style={{ opacity: 0.5, marginRight: 4 }}
                      >
                        Supported types:
                      </DevHubTypography>
                      {(
                        [
                          'url',
                          'text',
                          'github',
                          'file',
                          'flow',
                          'thread',
                          'agent',
                          'mcp',
                        ] as ContextType[]
                      ).map(type => (
                        <DevHubChip
                          key={type}
                          label={CONTEXT_TYPE_LABELS[type]}
                          chipColor={
                            entryChipColor(type as ContextEntry['type']) as any
                          }
                          chipSize="small"
                          noMargins
                        />
                      ))}
                    </DevHubBox>

                    {entriesHook.loadingEntries && (
                      <DevHubBox display="flex" justifyContent="center" p={2}>
                        <DevHubCircularProgress size={24} />
                      </DevHubBox>
                    )}
                    {!entriesHook.loadingEntries &&
                      entriesHook.channelEntries.length === 0 && (
                        <DevHubTypography
                          variant="body2"
                          style={{ opacity: 0.5 }}
                        >
                          No context sources for this channel yet.
                        </DevHubTypography>
                      )}
                    {!entriesHook.loadingEntries &&
                      entriesHook.channelEntries.length > 0 && (
                        <DevHubBox
                          display="flex"
                          flexDirection="column"
                          style={{ gap: 8 }}
                        >
                          {entriesHook.channelEntries.map((entry, i) => {
                            const isActive = entry.active !== false;
                            return (
                              <DevHubBox
                                key={i}
                                display="flex"
                                alignItems="center"
                                style={{
                                  gap: 12,
                                  padding: '10px 14px',
                                  borderRadius: 8,
                                  border: `1px solid ${isActive ? (theme.palette.devhub?.purple ?? '#7c4dff') : '#e0e0e0'}`,
                                  background: activeRowBg(
                                    isActive,
                                    theme.palette.type === 'dark',
                                    '124,77,255',
                                  ),
                                  opacity: isActive ? 1 : 0.6,
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <DevHubSwitch
                                  checked={isActive}
                                  size="small"
                                  disabled={!canEditSelectedChannelContext}
                                  onChange={() =>
                                    entriesHook.toggleEntry(
                                      selectedChannel!,
                                      i,
                                      numericAppId,
                                    )
                                  }
                                />
                                <DevHubChip
                                  label={CONTEXT_TYPE_LABELS[entry.type]}
                                  chipColor={entryChipColor(entry.type) as any}
                                  chipSize="small"
                                  noMargins
                                />
                                <DevHubTypography
                                  variant="body2"
                                  style={{
                                    flex: 1,
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    wordBreak: 'break-all',
                                    maxHeight: 60,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    textDecoration: isActive
                                      ? 'none'
                                      : 'line-through',
                                  }}
                                >
                                  {entryLabel(entry)}
                                </DevHubTypography>
                                {entry.type === 'github' && (
                                  <DevHubTooltip
                                    title={
                                      // eslint-disable-next-line no-nested-ternary
                                      entriesHook.refreshingGithubKey ===
                                      `${entry.org}/${entry.repo}`
                                        ? 'Starting refresh...'
                                        : // eslint-disable-next-line no-nested-ternary
                                          entriesHook.queuedGithubKey ===
                                            `${entry.org}/${entry.repo}`
                                          ? 'Refresh queued'
                                          : 'Refresh repo'
                                    }
                                    placement="top"
                                  >
                                    <DevHubIconButton
                                      size="small"
                                      disabled={
                                        !canEditSelectedChannelContext ||
                                        entriesHook.refreshingGithubKey ===
                                          `${entry.org}/${entry.repo}`
                                      }
                                      onClick={() =>
                                        entriesHook.refreshGithubEntry(
                                          selectedChannel!,
                                          entry,
                                          numericAppId,
                                        )
                                      }
                                    >
                                      {/* eslint-disable-next-line no-nested-ternary */}
                                      {entriesHook.refreshingGithubKey ===
                                      `${entry.org}/${entry.repo}` ? (
                                        <DevHubCircularProgress size={14} />
                                      ) : entriesHook.queuedGithubKey ===
                                        `${entry.org}/${entry.repo}` ? (
                                        <CheckIcon fontSize="small" />
                                      ) : (
                                        <RefreshIcon fontSize="small" />
                                      )}
                                    </DevHubIconButton>
                                  </DevHubTooltip>
                                )}
                                {(entry.type === 'flow' ||
                                  entry.type === 'agent' ||
                                  entry.type === 'mcp') &&
                                  entry.entryKey && (
                                    <DevHubTooltip
                                      title="Edit credentials"
                                      placement="top"
                                    >
                                      <DevHubIconButton
                                        size="small"
                                        disabled={
                                          !canEditSelectedChannelContext
                                        }
                                        onClick={() => {
                                          entryCredHook.reset();
                                          entryCredHook.loadStatus(
                                            selectedChannel!,
                                            entry.entryKey!,
                                          );
                                          setEntryCredDialogEntry({
                                            entryKey: entry.entryKey!,
                                            label: entryLabel(entry),
                                          });
                                        }}
                                      >
                                        <LockOutlinedIcon fontSize="small" />
                                      </DevHubIconButton>
                                    </DevHubTooltip>
                                  )}
                                <DevHubTooltip title="Edit" placement="top">
                                  <DevHubIconButton
                                    size="small"
                                    disabled={!canEditSelectedChannelContext}
                                    onClick={() => {
                                      setEditingEntry({
                                        index:
                                          entriesHook.channelEntries.indexOf(
                                            entry,
                                          ),
                                        entry,
                                      });
                                      setEditingFileObj(null);
                                      setEditingFileName(
                                        entry.type === 'file'
                                          ? (entry as any).value
                                          : null,
                                      );
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </DevHubIconButton>
                                </DevHubTooltip>
                                <DevHubTooltip title="Remove" placement="top">
                                  <DevHubIconButton
                                    size="small"
                                    disabled={!canEditSelectedChannelContext}
                                    onClick={() =>
                                      entriesHook.removeEntry(
                                        selectedChannel!,
                                        i,
                                        numericAppId,
                                      )
                                    }
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </DevHubIconButton>
                                </DevHubTooltip>
                              </DevHubBox>
                            );
                          })}
                        </DevHubBox>
                      )}
                  </>
                )}
              </DevHubBox>
            )
          }
        />
      </DevHubGrid>

      {/* Remove credential confirmation dialog */}
      <DevHubDialog
        open={credHook.confirmRemoveOpen}
        onClose={() => credHook.setConfirmRemoveOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DevHubDialogTitle>Remove Credentials?</DevHubDialogTitle>
        <DevHubDialogContent>
          <DevHubTypography variant="body2">
            This will permanently delete the stored SAT credentials for this
            channel. Any FLOW:Context or agent integrations relying on them will
            stop working.
          </DevHubTypography>
        </DevHubDialogContent>
        <DevHubDialogActions>
          <DevHubButton
            variant="outlined"
            color="secondary"
            height="large"
            onClick={() => credHook.setConfirmRemoveOpen(false)}
          >
            Cancel
          </DevHubButton>
          <DevHubButton
            variant="contained"
            buttonColor="red"
            textColor="white"
            height="large"
            onClick={() => credHook.removeCredential(selectedChannel!)}
            disabled={credHook.credRemoving}
            startIcon={
              credHook.credRemoving ? (
                <DevHubCircularProgress size={14} />
              ) : (
                <DeleteIcon fontSize="small" />
              )
            }
          >
            {credHook.credRemoving ? 'Removing…' : 'Remove'}
          </DevHubButton>
        </DevHubDialogActions>
      </DevHubDialog>

      {/* ── Entry Credentials Dialog (a2a agent / FLOW:Context) ── */}
      <DevHubDialog
        open={!!entryCredDialogEntry}
        onClose={() => setEntryCredDialogEntry(null)}
        maxWidth="xs"
        fullWidth
      >
        <DevHubDialogTitle>Entry Credentials</DevHubDialogTitle>
        <DevHubDialogContent>
          <DevHubBox
            display="flex"
            flexDirection="column"
            style={{ gap: 12, paddingTop: 8 }}
          >
            <DevHubTypography variant="caption" style={{ opacity: 0.6 }}>
              Credentials for <strong>{entryCredDialogEntry?.label}</strong>.
              Leave blank to use the channel or server default.
            </DevHubTypography>
            {entryCredHook.hasCredentials && (
              <DevHubChip
                label="Credentials saved"
                chipColor="success"
                chipSize="small"
                noMargins
              />
            )}
            {entryCredHook.error && (
              <DevHubAlert severity="error">{entryCredHook.error}</DevHubAlert>
            )}
            <EntryCredentialsFields
              value={entryCredHook.creds}
              onChange={entryCredHook.setCreds}
              tokenUrlError={entryCredHook.tokenUrlError}
            />
          </DevHubBox>
        </DevHubDialogContent>
        <DevHubDialogActions>
          {entryCredHook.hasCredentials && (
            <DevHubButton
              variant="outlined"
              buttonColor="red"
              textColor="red"
              height="large"
              onClick={() =>
                entryCredHook.removeCredential(
                  selectedChannel!,
                  entryCredDialogEntry!.entryKey,
                  numericAppId,
                )
              }
              disabled={entryCredHook.removing}
              startIcon={
                entryCredHook.removing ? (
                  <DevHubCircularProgress size={14} />
                ) : (
                  <DeleteIcon fontSize="small" />
                )
              }
            >
              {entryCredHook.removing ? 'Removing…' : 'Remove'}
            </DevHubButton>
          )}
          <DevHubButton
            variant="outlined"
            color="secondary"
            height="large"
            onClick={() => setEntryCredDialogEntry(null)}
          >
            Cancel
          </DevHubButton>
          <DevHubButton
            variant="contained"
            color="primary"
            height="large"
            onClick={async () => {
              await entryCredHook.saveCredential(
                selectedChannel!,
                entryCredDialogEntry!.entryKey,
                numericAppId,
              );
              if (!entryCredHook.error) setEntryCredDialogEntry(null);
            }}
            disabled={
              entryCredHook.saving ||
              !entryCredHook.creds.tokenUrl.trim() ||
              !entryCredHook.creds.clientId.trim() ||
              !entryCredHook.creds.clientSecret.trim()
            }
            startIcon={
              entryCredHook.saving ? (
                <DevHubCircularProgress size={14} />
              ) : undefined
            }
          >
            {entryCredHook.saving ? 'Saving…' : 'Save'}
          </DevHubButton>
        </DevHubDialogActions>
      </DevHubDialog>

      {/* ── Edit Context Dialog ── */}
      <DevHubDialog
        open={!!editingEntry}
        onClose={closeEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DevHubDialogTitle>Edit Context</DevHubDialogTitle>
        <DevHubDialogContent>
          {editingEntry && (
            <DevHubBox
              display="flex"
              flexDirection="column"
              style={{ gap: 16, paddingTop: 12 }}
            >
              <DevHubBox>
                <DevHubTypography variant="caption" style={{ opacity: 0.6 }}>
                  Type:{' '}
                  <strong>
                    {CONTEXT_TYPE_LABELS[editingEntry.entry.type]}
                  </strong>
                </DevHubTypography>
              </DevHubBox>

              {(editingEntry.entry.type === 'url' ||
                editingEntry.entry.type === 'text') && (
                <DevHubTextField
                  label={
                    editingEntry.entry.type === 'url' ? 'URL' : 'Text Content'
                  }
                  fullWidth
                  multiline={editingEntry.entry.type === 'text'}
                  minRows={editingEntry.entry.type === 'text' ? 4 : 1}
                  value={(editingEntry.entry as any).value || ''}
                  onChange={(e: any) => {
                    setEditingEntry({
                      ...editingEntry,
                      entry: {
                        ...editingEntry.entry,
                        value: e.target.value,
                      } as ContextEntry,
                    });
                  }}
                  placeholder={
                    editingEntry.entry.type === 'url'
                      ? 'https://example.com'
                      : 'Enter text content…'
                  }
                  size="small"
                />
              )}

              {editingEntry.entry.type === 'github' && (
                <>
                  <DevHubTextField
                    label="Organization"
                    fullWidth
                    value={(editingEntry.entry as any).org || ''}
                    onChange={(e: any) => {
                      setEditingEntry({
                        ...editingEntry,
                        entry: {
                          ...editingEntry.entry,
                          org: e.target.value,
                        } as ContextEntry,
                      });
                    }}
                    placeholder="org-name"
                    size="small"
                  />
                  <DevHubTextField
                    label="Repository"
                    fullWidth
                    value={(editingEntry.entry as any).repo || ''}
                    onChange={(e: any) => {
                      setEditingEntry({
                        ...editingEntry,
                        entry: {
                          ...editingEntry.entry,
                          repo: e.target.value,
                        } as ContextEntry,
                      });
                    }}
                    placeholder="repo-name"
                    size="small"
                  />
                  <DevHubTextField
                    label="Path (optional)"
                    fullWidth
                    value={(editingEntry.entry as any).path || ''}
                    onChange={(e: any) => {
                      setEditingEntry({
                        ...editingEntry,
                        entry: {
                          ...editingEntry.entry,
                          path: e.target.value,
                        } as ContextEntry,
                      });
                    }}
                    placeholder="docs/ or leave blank for root"
                    size="small"
                  />
                </>
              )}

              {editingEntry.entry.type === 'flow' && (
                <>
                  <DevHubTextField
                    label="Organization"
                    fullWidth
                    value={(editingEntry.entry as any).org || ''}
                    onChange={(e: any) => {
                      setEditingEntry({
                        ...editingEntry,
                        entry: {
                          ...editingEntry.entry,
                          org: e.target.value,
                        } as ContextEntry,
                      });
                    }}
                    placeholder="org-name"
                    size="small"
                  />
                  <DevHubTextField
                    label="Pool ID"
                    fullWidth
                    value={(editingEntry.entry as any).poolId || ''}
                    onChange={(e: any) => {
                      setEditingEntry({
                        ...editingEntry,
                        entry: {
                          ...editingEntry.entry,
                          poolId: e.target.value,
                        } as ContextEntry,
                      });
                    }}
                    placeholder="pool-id"
                    size="small"
                  />
                </>
              )}

              {editingEntry.entry.type === 'mcp' && (
                <>
                  <DevHubTextField
                    label="MCP server URL"
                    fullWidth
                    value={(editingEntry.entry as any).url || ''}
                    onChange={(e: any) => {
                      setEditingEntry({
                        ...editingEntry,
                        entry: {
                          ...editingEntry.entry,
                          url: e.target.value,
                        } as ContextEntry,
                      });
                    }}
                    placeholder="https://my-mcp-server.internal.com/mcp"
                    size="small"
                  />
                  <DevHubTextField
                    label="Label (optional)"
                    fullWidth
                    value={(editingEntry.entry as any).label || ''}
                    onChange={(e: any) => {
                      setEditingEntry({
                        ...editingEntry,
                        entry: {
                          ...editingEntry.entry,
                          label: e.target.value || undefined,
                        } as ContextEntry,
                      });
                    }}
                    placeholder="e.g. Kong Metrics"
                    size="small"
                  />
                </>
              )}

              {editingEntry.entry.type === 'file' && (
                <DevHubBox
                  display="flex"
                  flexDirection="column"
                  style={{ gap: 12 }}
                >
                  <label style={{ display: 'inline-flex', cursor: 'pointer' }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '5px 15px',
                        border: '1px solid',
                        borderRadius: 4,
                        fontSize: 14,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      {editingFileName ??
                        (editingEntry.entry as any).value ??
                        'Choose file'}
                    </span>
                    <input
                      type="file"
                      hidden
                      accept=".txt,.md,.json,.yaml,.yml,.pdf,application/pdf"
                      onChange={handleEditFileChange}
                    />
                  </label>
                  <DevHubTypography variant="caption" style={{ opacity: 0.6 }}>
                    Choose a replacement file. PDF uploads are re-indexed for
                    this channel.
                  </DevHubTypography>
                </DevHubBox>
              )}
            </DevHubBox>
          )}
        </DevHubDialogContent>
        <DevHubDialogActions>
          <DevHubButton
            variant="outlined"
            color="secondary"
            height="large"
            onClick={closeEditDialog}
          >
            Cancel
          </DevHubButton>
          <DevHubButton
            variant="contained"
            color="primary"
            height="large"
            onClick={async () => {
              if (editingEntry) {
                setEditingSaving(true);
                try {
                  let nextEntry = editingEntry.entry;

                  if (editingEntry.entry.type === 'file' && editingFileObj) {
                    if (!selectedChannel || selectedChannel === '__all__') {
                      setFetchError('No channel selected for file upload.');
                      return;
                    }

                    const { token } = await identityApi.getCredentials();
                    const formData = new FormData();
                    formData.append('file', editingFileObj);
                    formData.append('platform', 'slack');

                    const res = await fetch(
                      `${backendUrl}/api/agent/channel/${selectedChannel}/index-file`,
                      {
                        method: 'POST',
                        headers: token
                          ? { Authorization: `Bearer ${token}` }
                          : {},
                        body: formData,
                      },
                    );

                    if (!res.ok) {
                      setFetchError('Failed to upload file.');
                      return;
                    }

                    nextEntry = {
                      ...editingEntry.entry,
                      value: editingFileObj.name,
                    } as ContextEntry;
                  }

                  const updated = entriesHook.channelEntries.map((e, idx) =>
                    idx === editingEntry.index ? nextEntry : e,
                  );
                  await saveChannelEntries(updated);
                  closeEditDialog();
                } finally {
                  setEditingSaving(false);
                }
              }
            }}
            disabled={!editingEntry || editingSaving}
            startIcon={
              editingSaving ? <DevHubCircularProgress size={14} /> : undefined
            }
          >
            {editingSaving ? 'Saving…' : 'Save'}
          </DevHubButton>
        </DevHubDialogActions>
      </DevHubDialog>

      {/* ── Add Context Dialog ── */}
      <AddContextDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        addType={addType}
        channelEntries={entriesHook.channelEntries}
        selectedChannel={selectedChannel}
        backendUrl={backendUrl}
        onSave={saveChannelEntries}
      />

      {/* ── FLOW:Context Pool Picker ── */}
      <FlowPoolPickerDialog
        open={flowHook.flowPickerOpen}
        onClose={() => flowHook.setFlowPickerOpen(false)}
        flowPools={flowHook.flowPools}
        flowPoolsLoading={flowHook.flowPoolsLoading}
        flowPoolsError={flowHook.flowPoolsError}
        selectedPool={flowHook.selectedPool}
        setSelectedPool={flowHook.setSelectedPool}
        flowSaving={flowHook.flowSaving}
        channelEntries={entriesHook.channelEntries}
        onLink={credentials =>
          flowHook.linkPool(
            selectedChannel ?? '',
            entriesHook.channelEntries,
            saveChannelEntries,
            credentials,
            numericAppId,
          )
        }
      />

      {/* ── MCP Server Picker ── */}
      <McpServerPickerDialog
        open={mcpHook.mcpPickerOpen}
        onClose={() => mcpHook.setMcpPickerOpen(false)}
        saving={mcpHook.mcpSaving}
        error={mcpHook.mcpError}
        onAdd={(url, label, credentials) =>
          mcpHook.addServer(
            selectedChannel ?? '',
            entriesHook.channelEntries,
            saveChannelEntries,
            url,
            label,
            credentials,
            numericAppId,
          )
        }
      />

      {/* ── Save success snackbar ── */}
      <DevHubSnackbar
        open={instrHook.saveSuccess}
        autoHideDuration={3000}
        onClose={() => instrHook.setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <DevHubAlert
          severity="success"
          onClose={() => instrHook.setSaveSuccess(false)}
        >
          Instructions saved.
        </DevHubAlert>
      </DevHubSnackbar>
    </DevHubGrid>
  );
};
